<script setup>
import { t } from "@/controllerLK/GlobusLocalization";
import {defineEmits, defineProps, ref, watchEffect} from "vue";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import {convertTimestampToData} from "@/tools/date-time-tools";
import { useNotificationsStore } from "@/store/notifications";
import CrossIcon from "@/components/Icons/CrossIcon.vue";
import LocationIcon from "@/components/Icons/LocationIcon.vue";
import IconTranslate from "@/components/Icons/IconTranslate.vue";
import IconTranslateFixed from "@/components/Icons/IconTranslateFixed.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import {importanceList} from "@/const/importance.list";

const newsFeedStore = useNewsFeedStore();

const props = defineProps({
  isNewsListOpened: {
    type: Boolean,
    default: false,
    required: true
  },
  newsItem: {
    type: [Object, null],
    required: true,
    default: () => {}
  },
  cityName: {
    type: String,
    required: true,
  }
});

const emit = defineEmits(["close-news-list"]);
const store = useNotificationsStore();

const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    store.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    store.createNotify(t("map_page.toast.clipboard_error"), "success");
  }
};

const isActiveTranslateNewsText = ref(false);
const isActiveNewsTextTranslated = ref(false);
const translatedNewsTitle = ref("");
const translatedNewsText = ref("");

watchEffect(() => {
  if (props.newsItem) {
    translatedNewsTitle.value = props.newsItem.title;
    translatedNewsText.value = props.newsItem.text;
  }
});

const translateNewsText = async () => {
  isActiveTranslateNewsText.value = true;
  if (isActiveNewsTextTranslated.value) {
    translatedNewsTitle.value = props.newsItem.title;
    translatedNewsText.value = props.newsItem.text;
    isActiveNewsTextTranslated.value = false;
  } else {
    const translated = await newsFeedStore.translateNewsText(
        translatedNewsTitle.value,
        translatedNewsText.value,
        props.item.id
    );

    translatedNewsTitle.value = translated.title;
    console.log(translated);
    translatedNewsText.value = translated.raw_text;
    isActiveNewsTextTranslated.value = true;
  }
  isActiveTranslateNewsText.value = false;
};
</script>

<template>
  <div
    v-if="isNewsListOpened"
    class="single-news-wrapper"
  >
    <div class="header">
      <div class="header__title">
        {{ translatedNewsTitle }}
      </div>
      <div class="header__buttons">
        <div
          class="header__button"
          :class="{'header__button--active': isActiveNewsTextTranslated}"
          @click="translateNewsText"
        >
          <LocalLoader
            v-if="isActiveTranslateNewsText"
            style="width: 18px; height: 18px"
          />
          <template v-else>
            <IconTranslateFixed v-if="isActiveNewsTextTranslated" />
            <IconTranslate v-else />
          </template>
        </div>
        <div
          class="header__button"
          @click="emit('close-news-list')"
        >
          <CrossIcon />
        </div>
      </div>
    </div>
    <div class="body scroll-single-news">
      <div class="full-card-header">
        <div class="full-card-header__date">
          {{ convertTimestampToData(props.newsItem.date) }}
        </div>
        <div class="full-card-header-items">
          <div class="full-card-header-items__location">
            <LocationIcon />
            {{ cityName }}
          </div>
          <div class="full-card-header-items__sentiment">
            <Component
              :is="props.newsItem.sentiment > 0 ? GoodIcon : props.newsItem.sentiment < 0 ? SadIcon : NeutralIcon"
              v-if="props.newsItem.sentiment !== null"
            />
          </div>
        </div>
      </div>
      <div
        class="full-card-body"
        v-html="translatedNewsText"
      />
    </div>

    <div class="footer">
      <div class="footer__description">
        <div class="description__item">
          Feed url:
          <a
            :href="newsItem.source"
            target="_blank"
          >{{ props.newsItem.domain }}</a>
        </div>

        <div
          class="description__item"
          @click="saveIdToClipboard(props.newsItem.id)"
        >
          id:
          <a>{{ props.newsItem.id }}</a>
        </div>
      </div>
      <div class="footer-importance">
        <Chip
          class="footer-importance__value"
          :model-value="true"
          :use-cross-icon="false"
          :color="importanceList[props.newsItem.importance].colorName"
        >
          {{ t('map_single_news_view.importance.' + importanceList[props.newsItem.importance].name) }}
        </Chip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/style/variables.scss";

.scroll-single-news {
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 27px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: variables.$blue-another;
    border: 10px solid transparent;
    background-clip: padding-box;
    border-radius: 30px;
  }
}

.single-news-wrapper {
  position: absolute;
  right: 0;
  bottom: 14px;
  display: flex;
  flex-direction: column;
  width: 537px;
  padding: 20px;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 4px 8px 0 #00000024;
  gap: 10px;
  max-height: calc(100% - 108px);
  z-index: 999;

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__description {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 10px;


      .description__item {
        display: inline;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 12px;
        font-weight: 400;
        color: #7B87A5;

        a {
          font-size: 12px;
          font-weight: 400;
          cursor: pointer;
          text-decoration: underline;
          color: #1F9AFF;
        }
      }
    }

    &-importance {
      display: flex;
      gap: 5px;
      align-items: center;

      &__date {
        display: inline;
        white-space: nowrap;
        font-weight: 400;
        font-size: 12px;
        color: #808080;
      }

      &__value {
        white-space: nowrap;
      }

    }
  }

  .body {
    display: flex;
    flex-direction: column;
    padding: 20px;
    border-radius: 8px;
    background: #F7F8FD;
    gap: 10px;

    .full-card-body {
      line-height: 19.6px;
    }

    .full-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;

      &__date {
        font-size: 14px;
        font-weight: 400;
        color: #808080;
      }

      &-items {
        display: flex;
        gap: 5px;

        &__location {
          text-align: center;
          display: inline;
          color: #7B87A5;
          background: #FFF;
          padding: 4px 6px;
          border-radius: 20px;
          height: 24px;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;

    &__title {
      font-size: 16px;
      line-height: 22px;
      font-weight: 500;
      color: #212327;
    }

    &__buttons {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    &__button {
      color: var(--icon-icon-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 36px;
      background: var(--button-secondary-neutral-default);
      cursor: pointer;
      border-radius: 50%;

      &:hover {
        color: #1F9AFF;
      }

      &--active {
        background: var(--icon-icon-primary);

        &:hover {
          background: var(--purple-purple80);
        }
      }
    }
  }
}

</style>